import { exactTermSource } from './viewer/termMarks';
/**
 * SPL XML Content Renderer
 *
 * Transforms HL7 SPL XML content to HTML for proper rendering of drug label sections.
 * Handles paragraphs, lists, formatting, tables, and images from GCP blobstore.
 */
import React, { useMemo, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { findInTab, useTab } from '../shell/TabContext';

interface SplContentRendererProps {
  /** Raw SPL XML content from content_xml field */
  contentXml: string | null;
  /** Fallback plain text content */
  contentText: string | null;
  /** Drug name (used for context) */
  drugName: string;
  /** Optional className for the container */
  className?: string;
  /** Optional highlight function for search matches (used for plain text fallback) */
  highlightFn?: (text: string) => React.ReactNode;
  /** Optional search terms to highlight in HTML content */
  highlightTerms?: string[];
  englishExact?: boolean;
  /** Optional per-term mark colors (parallel to highlightTerms by index). When
   * omitted, terms use the default amber highlight. */
  highlightColors?: (string | undefined)[];
  /** Pre-formatted HTML content (from DOCX parsing) - used when contentXml is null */
  contentHtml?: string | null;
  /** Evidence chunk text to highlight with distinct styling (from V2 search click-through) */
  evidenceChunkText?: string | null;
}

// Images are now served via DailyMed URLs injected into content_xml by the backfill script

/** One CJK character (the ranges of the backend's normalize_ja_v1). */
const CJK_CHAR = /[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uFF00-\uFFEF]/;
// CJK kanji outside the BMP (Extension B and later: surrogate pairs in JS).
const isCjkChar = (ch: string | undefined): boolean => {
  if (!ch) return false;
  const cp = ch.codePointAt(0) ?? 0;
  return CJK_CHAR.test(ch) || (cp >= 0x20000 && cp <= 0x3134f);
};

export interface TermRange { start: number; end: number; termIndex: number }

/**
 * Find the non-overlapping match ranges of `terms` in `text`. `termIndex` is
 * the index into the caller's `terms`. Shared by the SPL (DOM) highlighter and
 * the plain-text path so both mark exactly the same spans.
 */
export function findTermRanges(text: string, terms: string[], englishExact = false): TermRange[] {
  if (!text || !terms || terms.length === 0) return [];

  // Build a per-term pattern. Plain terms match as whole words (\b…\b).
  // Wildcard terms (``hospital*`` / ``*fix`` / ``*fix*``) expand the ``*``
  // side(s) to ``\w*`` so the FULL matched word is captured & marked
  // (``*fix*`` → "fixed", ``hospital*`` → "hospitalization") — instead of
  // escaping the ``*`` into a literal that never matches.
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Compile one regex PER term (not a single alternation) so each match can be
  // attributed back to its term index → color. Order is preserved.
  const compiled = terms.map((term, idx) => {
    const exact = englishExact ? exactTermSource(term) : null;
    if (exact) return { termIndex: idx, re: new RegExp(exact, 'gi') };
    const lead = term.startsWith('*');
    const trail = term.endsWith('*');
    const bare = term.replace(/^\*+|\*+$/g, '').trim();
    if (!bare) return null;
    // Japanese / CJK term (jp_ja search, plan 2026-10-09 §13): ``\b`` never
    // matches between two CJK characters, and the source HTML has stray
    // extraction spaces inside phrases (``間質性 肺炎``) that the search
    // normalizer removed. So a CJK term drops ``\b`` on its CJK edge(s) and
    // lets every character be followed by optional whitespace. A term with no
    // CJK character builds exactly the same regex as before.
    const chars = Array.from(bare);
    if (chars.some(isCjkChar)) {
      const coreJa = Array.from(bare.replace(/\s+/g, '')).map(esc).join('\\s*');
      const leftJa = lead ? '\\w*' : isCjkChar(chars[0]) ? '' : '\\b';
      const rightJa = trail ? '\\w*' : isCjkChar(chars[chars.length - 1]) ? '' : '\\b';
      return { termIndex: idx, re: new RegExp(`${leftJa}${coreJa}${rightJa}`, 'gi') };
    }
    // Multi-word phrase → join words with ``\s+`` so wrapped lines / runs of
    // whitespace between words still match.
    const core = bare.split(/\s+/).map(esc).join('\\s+');
    const left = lead ? '\\w*' : '\\b';
    const right = trail ? '\\w*' : '\\b';
    return { termIndex: idx, re: new RegExp(`${left}${core}${right}`, 'gi') };
  }).filter(Boolean) as { termIndex: number; re: RegExp }[];
  if (compiled.length === 0) return [];

  let ranges: TermRange[] = [];
  for (const { termIndex, re } of compiled) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m[0].length === 0) { re.lastIndex++; continue; }
      ranges.push({ start: m.index, end: m.index + m[0].length, termIndex });
    }
  }
  // Resolve overlaps between terms: earliest start wins, then longest match.
  // Greedily keep non-overlapping ranges (mirrors the old single-alternation
  // left-to-right behavior while letting different terms keep their colors).
  ranges.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const kept: TermRange[] = [];
  let lastEnd = -1;
  for (const r of ranges) {
    if (r.start >= lastEnd) { kept.push(r); lastEnd = r.end; }
  }
  return kept;
}

/** Elements whose edges separate words when the highlighter joins text nodes. */
const BLOCK_TAGS = new Set(['P', 'LI', 'UL', 'OL', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH', 'DIV', 'BR']);

/**
 * Apply search term highlighting to a DOM element in-place.
 * Injects <mark> tags into text nodes, preserving HTML structure.
 */
function applySearchTermHighlight(
  container: HTMLElement,
  terms: string[],
  // Optional per-term mark background, parallel to `terms` by index. When a
  // color is given for a term, its matches get that inline background (so
  // multi-row keyword queries can be told apart by color); when omitted, the
  // term keeps the classic amber class. Existing single-term / evidence
  // callers pass no colors and are unchanged.
  colors?: (string | undefined)[],
  // Opt-in: treat block-element edges as word boundaries (export only). Off =
  // the pre-existing behaviour for every other caller.
  opts?: { blockBoundaries?: boolean; englishExact?: boolean },
): void {
  if (!terms || terms.length === 0) return;

  // Collect ALL descendant text nodes + the concatenated string, so a phrase
  // split across inline element boundaries (e.g. "the <sup>2</sup> treatment
  // of", footnote refs, cross-ref links) is matched as a whole. The previous
  // implementation ran the regex on each text node separately, so multi-word
  // phrases that the SPL renders across inline tags never matched (single words
  // always sit in one node, which is why they worked and phrases didn't).
  const SKIP = new Set(['SCRIPT', 'STYLE', 'MARK', 'CODE', 'PRE']);
  const nodes: { node: Text; start: number; end: number }[] = [];
  let full = '';
  const collect = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const start = full.length;
      full += node.textContent || '';
      nodes.push({ node: node as Text, start, end: full.length });
    } else if (node.nodeType === Node.ELEMENT_NODE && opts?.englishExact &&
      (node as Element).tagName === 'MARK' && (node as Element).hasAttribute('data-comment-id')) {
      // Comment marks own their contents, but their text still determines
      // whether a phrase or numeric boundary is a real match.
      full += node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE && !SKIP.has((node as Element).tagName)) {
      // A block boundary is a word boundary: "<td>12</td><td>rash</td>" must
      // not read as "12rash" (the phantom space belongs to no text node).
      const block = !!opts?.blockBoundaries && BLOCK_TAGS.has((node as Element).tagName);
      if (block) full += ' ';
      node.childNodes.forEach(collect);
      if (block) full += ' ';
    }
  };
  collect(container);
  if (!full) return;

  const ranges = findTermRanges(full, terms, opts?.englishExact);
  if (!ranges.length) return;

  // Wrap the overlapping slice inside each text node. Reverse order so earlier
  // nodes' references stay valid as later ones are replaced.
  for (let i = nodes.length - 1; i >= 0; i--) {
    const tn = nodes[i]!;
    const overlaps = ranges
      .filter((r) => r.end > tn.start && r.start < tn.end)
      .map((r) => ({
        s: Math.max(r.start, tn.start) - tn.start,
        e: Math.min(r.end, tn.end) - tn.start,
        color: colors?.[r.termIndex],
        cont: r.start < tn.start,
      }))
      .sort((a, b) => a.s - b.s);
    if (!overlaps.length) continue;
    const text = tn.node.textContent || '';
    const fragment = document.createDocumentFragment();
    let last = 0;
    for (const { s, e, color, cont } of overlaps) {
      if (s > last) fragment.appendChild(document.createTextNode(text.slice(last, s)));
      const mark = document.createElement('mark');
      if (color) {
        mark.className = 'search-highlight px-0.5 rounded';
        mark.style.backgroundColor = color;
      } else {
        mark.className = 'search-highlight bg-amber-200 px-0.5 rounded';
      }
      // R5.3: later pieces of a match split across inline elements.
      if (cont) mark.className += ' search-highlight-cont';
      mark.textContent = text.slice(s, e);
      fragment.appendChild(mark);
      last = e;
    }
    if (last < text.length) fragment.appendChild(document.createTextNode(text.slice(last)));
    tn.node.parentNode?.replaceChild(fragment, tn.node);
  }
}


/**
 * Re-color an already-highlighted HTML snippet (e.g. a backend ts_headline with
 * single-color <mark>s) so each query term gets its own color. Unwraps any
 * existing <mark> first, then re-runs the term highlighter with per-term colors.
 * Used by the keyword results excerpts so the results, compare, and export views
 * all share one coloring scheme. Browser-only (uses `document`). Pass an
 * ALREADY-SANITIZED string — this only inserts <mark> wrappers around matched
 * text (textContent only) + inline background, so it adds no new XSS surface.
 */
export function recolorHighlightedHtml(
  html: string,
  terms: string[],
  colors?: (string | undefined)[],
): string {
  if (!terms.length) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('mark').forEach((m) => {
    m.replaceWith(document.createTextNode(m.textContent || ''));
  });
  applySearchTermHighlight(div, terms, colors);
  return div.innerHTML;
}

/**
 * Normalize text for fuzzy matching: collapse whitespace, lowercase, trim
 */
function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').toLowerCase().trim();
}

/**
 * Apply evidence chunk highlighting to a DOM element in-place.
 * Uses fuzzy text matching to find overlapping block elements and adds
 * the evidence-chunk-highlight CSS class (left border + background).
 * Safety: classList.add only adds a CSS class, no script injection possible.
 */
function applyEvidenceChunkHighlight(container: HTMLElement, chunkText: string): void {
  const normalizedChunk = normalizeText(chunkText);
  if (normalizedChunk.length < 20) return;

  const startAnchor = normalizedChunk.slice(0, Math.min(60, normalizedChunk.length));
  const endAnchor = normalizedChunk.length > 60
    ? normalizedChunk.slice(-Math.min(60, normalizedChunk.length))
    : startAnchor;

  // Check p, li, table rows, heading-like divs (EU labels may include headers in chunks)
  const blocks = container.querySelectorAll('p, li, div.spl-paragraph, tr, h3, h4');
  let startIdx = -1;
  let endIdx = -1;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks.item(i);
    if (!block) continue;
    const blockText = normalizeText(block.textContent || '');
    if (blockText.length < 3) continue;

    if (startIdx === -1 && blockText.includes(startAnchor.slice(0, 40))) {
      startIdx = i;
    }

    if (startIdx !== -1 && blockText.includes(endAnchor.slice(-40))) {
      endIdx = i;
      break;
    }
  }

  if (startIdx !== -1 && endIdx === -1) endIdx = startIdx;

  if (startIdx !== -1) {
    for (let i = startIdx; i <= endIdx; i++) {
      const block = blocks.item(i);
      if (block) block.classList.add('evidence-chunk-highlight');
    }
  }
}

/**
 * Process footnotes within a block of content
 * Returns the processed HTML with footnote references and the footnotes section HTML
 */
function processFootnotes(
  content: string,
  tableIndex: number
): { html: string; footnotesHtml: string } {
  const footnotes: Map<string, { number: number; text: string }> = new Map();
  let footnoteCounter = 0;
  const prefix = tableIndex > 0 ? `t${tableIndex}-` : '';

  let html = content;

  // First pass: extract all footnote definitions and assign numbers
  html = html.replace(
    /<footnote\s+ID="([^"]+)"([^>]*)>([\s\S]*?)<\/footnote>/gi,
    (_match, id, _attrs, text) => {
      footnoteCounter++;
      const cleanText = text.replace(/<[^>]+>/g, '').trim();
      footnotes.set(id, { number: footnoteCounter, text: cleanText });
      const fnId = `${prefix}${footnoteCounter}`;
      return `<a href="#fn-${fnId}" id="fnref-${fnId}" class="footnote-ref text-indigo-600 hover:underline" data-footnote="${fnId}"><sup>[${footnoteCounter}]</sup></a>`;
    }
  );

  // Second pass: replace footnote references with the same number
  html = html.replace(
    /<footnoteRef\s+IDREF="([^"]+)"([^>]*)\/?>/gi,
    (_match, idref) => {
      const footnote = footnotes.get(idref);
      if (footnote) {
        const fnId = `${prefix}${footnote.number}`;
        return `<a href="#fn-${fnId}" class="footnote-ref text-indigo-600 hover:underline" data-footnote="${fnId}"><sup>[${footnote.number}]</sup></a>`;
      }
      return '';
    }
  );

  // Third pass: handle footnotes WITHOUT ID attribute (anonymous footnotes)
  // These appear as <footnote>text</footnote> without an ID attribute
  html = html.replace(
    /<footnote(?![^>]*\sID=)[^>]*>([\s\S]*?)<\/footnote>/gi,
    (_match, text) => {
      footnoteCounter++;
      const cleanText = text.replace(/<[^>]+>/g, '').trim();
      const anonId = `anon-${footnoteCounter}`;
      footnotes.set(anonId, { number: footnoteCounter, text: cleanText });
      const fnId = `${prefix}${footnoteCounter}`;
      return `<a href="#fn-${fnId}" id="fnref-${fnId}" class="footnote-ref text-indigo-600 hover:underline" data-footnote="${fnId}"><sup>*</sup></a>`;
    }
  );

  // Build footnotes section HTML
  let footnotesHtml = '';
  if (footnotes.size > 0) {
    const footnotesList = Array.from(footnotes.entries())
      .sort((a, b) => a[1].number - b[1].number)
      .map(([_id, { number, text }]) => {
        const fnId = `${prefix}${number}`;
        return `<div id="fn-${fnId}" class="footnote-item text-sm text-slate-600 mb-2 pl-6 relative">
          <a href="#fnref-${fnId}" class="absolute left-0 top-0 text-indigo-600 font-medium hover:underline" data-backref="${fnId}">[${number}]</a>
          <span>${text}</span>
        </div>`;
      })
      .join('');
    footnotesHtml = `<div class="footnotes-section mt-3 pt-3 border-t border-slate-200 text-sm">${footnotesList}</div>`;
  }

  return { html, footnotesHtml };
}

/**
 * Transform SPL XML to HTML
 */
function transformSplXmlToHtml(
  xml: string,
): string {
  let html = xml;

  // Remove XML namespaces for easier processing
  html = html.replace(/xmlns(:[a-z]+)?="[^"]*"/gi, '');

  // Process tables with their footnotes separately
  let tableIndex = 0;
  html = html.replace(
    /<table([^>]*)>([\s\S]*?)<\/table>/gi,
    (_match, attrs, tableContent) => {
      tableIndex++;
      const { html: processedContent, footnotesHtml } = processFootnotes(tableContent, tableIndex);
      return `<table${attrs}>${processedContent}</table>${footnotesHtml}`;
    }
  );

  // Process any remaining footnotes outside tables (with index 0 for global)
  const { html: finalHtml, footnotesHtml: globalFootnotes } = processFootnotes(html, 0);
  html = finalHtml;

  // Append global footnotes at the end if any exist
  if (globalFootnotes) {
    html += globalFootnotes;
  }

  // Transform SPL elements to HTML

  // Paragraphs: <paragraph> → <p> with spacing
  html = html.replace(/<paragraph([^>]*)>/gi, '<p class="mb-3 leading-relaxed"$1>');
  html = html.replace(/<\/paragraph>/gi, '</p>');

  // Content with styleCode (underline, bold, italics) - use spans with classes for consistent closing
  html = html.replace(/<content\s+styleCode="underline"([^>]*)>/gi, '<span class="underline"$1>');
  html = html.replace(/<content\s+styleCode="bold"([^>]*)>/gi, '<span class="font-bold"$1>');
  html = html.replace(/<content\s+styleCode="italics"([^>]*)>/gi, '<span class="italic"$1>');
  html = html.replace(/<content\s+styleCode="emphasis"([^>]*)>/gi, '<span class="italic"$1>');
  // Generic content tags without specific styling
  html = html.replace(/<content([^>]*)>/gi, '<span$1>');
  html = html.replace(/<\/content>/gi, '</span>');

  // Strip <caption> from list items (they duplicate CSS markers, causing "1. 1." or "• •" display)
  // SPL XML includes explicit captions like <caption>1.</caption> or <caption>•</caption>
  // which duplicate the CSS list-style markers
  html = html.replace(/<list[^>]*>[\s\S]*?<\/list>/gi, (match) => {
    return match.replace(/<caption>[^<]*<\/caption>/gi, '');
  });

  // Lists: <list> → <ul> or <ol>
  // Use markers to track list types, then convert closing tags appropriately
  // Step 1: Mark ordered lists with a temporary marker
  html = html.replace(/<list\s+listType="ordered"([^>]*)>/gi, '<!--OL--><ol class="list-decimal ml-6 space-y-1"$1>');
  // Handle unordered lists with various styles
  html = html.replace(/<list\s+listType="unordered"\s+styleCode="[Cc]ircle"([^>]*)>/gi, '<!--UL--><ul class="list-[circle] ml-6 space-y-1"$1>');
  html = html.replace(/<list\s+listType="unordered"\s+styleCode="[Ss]quare"([^>]*)>/gi, '<!--UL--><ul class="list-[square] ml-6 space-y-1"$1>');
  html = html.replace(/<list\s+listType="unordered"([^>]*)>/gi, '<!--UL--><ul class="list-disc ml-6 space-y-1"$1>');
  // Fallback for lists without explicit type
  html = html.replace(/<list([^>]*)>/gi, '<!--UL--><ul class="list-disc ml-6 space-y-1"$1>');

  // Step 2: Process closing tags using a stack to match with correct list type
  const listStack: string[] = [];
  let result = '';
  let lastIndex = 0;
  const markerRegex = /<!--(OL|UL)-->|<\/list>/gi;
  let match;

  while ((match = markerRegex.exec(html)) !== null) {
    result += html.substring(lastIndex, match.index);

    if (match[0].startsWith('<!--') && match[1]) {
      // Opening marker - push to stack and include the marker
      listStack.push(match[1]); // 'OL' or 'UL'
      result += match[0];
    } else if (!match[0].startsWith('<!--')) {
      // Closing </list> - pop from stack to determine correct closing tag
      const listType = listStack.pop();
      result += listType === 'OL' ? '</ol>' : '</ul>';
    }
    lastIndex = match.index + match[0].length;
  }
  result += html.substring(lastIndex);
  html = result;

  // Step 3: Remove the temporary markers
  html = html.replace(/<!--(OL|UL)-->/g, '');

  // List items: <item> → <li>
  html = html.replace(/<item([^>]*)>/gi, '<li class="text-slate-700"$1>');
  html = html.replace(/<\/item>/gi, '</li>');

  // Internal links: <linkHtml href="#S1.1"> → <a> with data-section for navigation
  // Extract section number from href like "#S1.1" or "#section-1.1" and add as data attribute
  html = html.replace(/<linkHtml\s+href="#([^"]*)"([^>]*)>/gi, (_match, href, rest) => {
    // Extract section number from patterns like "S1.1", "section-1.1", "LINK_1.1"
    const sectionMatch = href.match(/(?:S|section-|LINK_)?(\d+(?:\.\d+)?)/i);
    const sectionNum = sectionMatch ? sectionMatch[1] : href;
    return `<a href="#" data-section="${sectionNum}" class="text-indigo-600 hover:underline cursor-pointer"${rest}>`;
  });
  // Handle external links (with http/https)
  html = html.replace(/<linkHtml\s+href="(https?:\/\/[^"]*)"([^>]*)>/gi, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:underline"$2>');
  html = html.replace(/<\/linkHtml>/gi, '</a>');

  // Superscript and subscript
  html = html.replace(/<sup([^>]*)>/gi, '<sup class="text-xs"$1>');
  html = html.replace(/<sub([^>]*)>/gi, '<sub class="text-xs"$1>');

  // Line breaks
  html = html.replace(/<br\s*\/?>/gi, '<br/>');

  // Images: After backfill, content_xml already contains <img src="https://dailymed.nlm.nih.gov/..."/>
  // Style any existing <img> tags with proper classes
  html = html.replace(
    /<img\s+/gi,
    '<img class="max-w-full h-auto my-4 border border-slate-200 rounded" loading="lazy" onerror="this.style.display=\'none\'" '
  );

  // Remove any remaining <renderMultiMedia> tags that weren't backfilled yet
  html = html.replace(/<renderMultiMedia[^>]*\/?>/gi, '');

  // Tables: preserve structure but add styling
  html = html.replace(/<table([^>]*)>/gi, '<table class="w-full border-collapse my-4 text-sm"$1>');
  html = html.replace(/<caption([^>]*)>/gi, '<caption class="text-left font-semibold text-slate-800 mb-2"$1>');
  html = html.replace(/<thead([^>]*)>/gi, '<thead class="bg-slate-100"$1>');
  html = html.replace(/<tfoot([^>]*)>/gi, '<tfoot class="bg-slate-50 text-xs text-slate-600"$1>');
  html = html.replace(/<tbody([^>]*)>/gi, '<tbody$1>');
  html = html.replace(/<tr([^>]*)>/gi, '<tr class="border-b border-slate-200"$1>');
  html = html.replace(/<th([^>]*)>/gi, '<th class="px-3 py-2 text-left font-semibold text-slate-700 border border-slate-300"$1>');
  html = html.replace(/<td([^>]*)>/gi, '<td class="px-3 py-2 text-slate-600 border border-slate-300"$1>');

  // Handle styleCode attributes on table cells
  html = html.replace(/styleCode="[^"]*Toprule[^"]*"/gi, '');
  html = html.replace(/styleCode="[^"]*Botrule[^"]*"/gi, '');
  html = html.replace(/styleCode="[^"]*Lrule[^"]*"/gi, '');
  html = html.replace(/styleCode="[^"]*Rrule[^"]*"/gi, '');

  // Transform <text> elements to <div> (SPL uses <text> as content wrapper)
  // This handles both outer wrappers and nested <text xmlns="..."> elements
  html = html.replace(/<text[^>]*>/gi, '<div>');
  html = html.replace(/<\/text>/gi, '</div>');

  // UK Patient Information Leaflet sub-headers: emc encodes 2nd-level
  // subsection titles (e.g. "Warnings and precautions", "Do not use X",
  // "What is in this leaflet") as
  //   <details><summary class="xpilSectionHeader2ndLevel">
  //     <div class="sectionWrapper">TITLE</div></summary> …body… </details>
  // DOMPurify drops <details>/<summary> (not in ALLOWED_TAGS) and there is
  // no inline style/<strong> on these titles, so without intervention they
  // collapse into plain body text and lose the visual hierarchy the leaflet
  // intends. Promote each xpilSectionHeader summary to an <h4> (same prose
  // style as the <p><strong> subheader promotion below), then unwrap the
  // <details> so the body that followed the summary renders in flow.
  html = html.replace(
    /<summary[^>]*class="[^"]*xpilSectionHeader[^"]*"[^>]*>([\s\S]*?)<\/summary>/gi,
    (_m, inner) => {
      const wrap = inner.match(
        /<div[^>]*class="[^"]*sectionWrapper[^"]*"[^>]*>([\s\S]*?)<\/div>/i,
      );
      const title = (wrap ? wrap[1] : inner).trim();
      return `<h4 class="font-semibold mt-4 mb-2 text-slate-900">${title}</h4>`;
    },
  );
  html = html.replace(/<\/?details[^>]*>/gi, '');

  // Subheader promotion: MHRA / EMA / Health Canada / TGA SPCs encode
  // subsection titles as ``<p><strong>Hyperglycaemia</strong></p>`` —
  // a paragraph whose entire content is one bold run. The source has no
  // <h*> tags at any level inside §4.4/§4.8/§5.x. Without intervention
  // these render visually flat (all paragraphs look the same).
  //
  // We detect the pattern and promote to ``<h4 class="font-semibold
  // mt-4 mb-2 text-slate-900">X</h4>`` so the prose styles give it the
  // hierarchy the regulator visually intends. ~89% of non-US drugs on
  // prod hit this pattern; the alternative (re-parse every UK / EU / CA /
  // AU drug from source) is a multi-hour operation for the same end
  // result.
  //
  // Heuristic: paragraph open tag, optional whitespace + tags-with-no-text,
  // a single <strong>...</strong>, optional whitespace, paragraph close.
  // We allow inline <sub> / <sup> inside the strong content (e.g.
  // ``HbA<sub>1C</sub>`` is a common subheader-end fragment).
  html = html.replace(
    /<p(?:\s+[^>]*)?>\s*<strong>([^<]+(?:<(?:sub|sup)>[^<]*<\/(?:sub|sup)>[^<]*)*)<\/strong>\s*<\/p>/gi,
    '<h4 class="font-semibold mt-4 mb-2 text-slate-900">$1</h4>',
  );

  // Clean up extra whitespace
  html = html.replace(/>\s+</g, '> <');
  html = html.trim();

  return html;
}

/**
 * Allowed HTML tags for DOMPurify sanitization
 */
const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li',
  'a',
  'sup', 'sub',
  'table', 'caption', 'thead', 'tfoot', 'tbody', 'tr', 'th', 'td', 'col', 'colgroup',
  'img',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];

/**
 * Allowed HTML attributes for DOMPurify sanitization
 */
const ALLOWED_ATTR = [
  'class', 'href', 'src', 'alt', 'loading', 'onerror',
  'colspan', 'rowspan', 'width', 'valign', 'align',
  'data-section', 'data-footnote', 'data-backref', 'id',
  'target', 'rel', 'title',
];

/**
 * Reusable: apply the SAME search-term + evidence-chunk highlighting to an
 * ALREADY-HTML string (no SPL transform). Used by non-SPL renderers — e.g. the
 * JP bilingual Compare panels, whose content_html is plain HTML, not SPL XML —
 * so they get the identical <mark> term boxes and `.evidence-chunk-highlight`
 * blue block as the SPL viewer. Browser-only (document). Returns the input
 * unchanged when there's nothing to highlight; '' for empty input.
 */
export function highlightHtmlString(
  html: string | null | undefined,
  terms: string[] = [],
  colors?: (string | undefined)[],
  evidenceChunkText?: string | null,
  opts?: { blockBoundaries?: boolean; englishExact?: boolean },
): string {
  if (!html) return '';
  if (!terms.length && !evidenceChunkText) return html;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  if (terms.length > 0) applySearchTermHighlight(tempDiv, terms, colors, opts);
  if (evidenceChunkText) applyEvidenceChunkHighlight(tempDiv, evidenceChunkText);
  return tempDiv.innerHTML;
}

/**
 * Reusable: SPL XML → sanitized, FORMATTED HTML string (tables, bullets, bold,
 * links) with the search terms highlighted (<mark>). Same pipeline the
 * component uses — for non-React consumers like the Compare export/snapshot so
 * the offline copy keeps the on-screen formatting. Browser-only (DOMPurify +
 * document). Falls back to '' when there's no XML.
 */
export function renderSplToHighlightedHtml(
  contentXml: string | null | undefined,
  terms: string[] = [],
  colors?: (string | undefined)[],
  evidenceChunkText?: string | null,
  englishExact = false,
): string {
  if (!contentXml) return '';
  const result = DOMPurify.sanitize(transformSplXmlToHtml(contentXml), {
    ALLOWED_TAGS: [...ALLOWED_TAGS, 'mark'],
    ALLOWED_ATTR,
    ADD_ATTR: ['loading', 'onerror', 'data-section', 'data-footnote', 'data-backref', 'id', 'class'],
  });
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = result;
  if (terms.length > 0) {
    applySearchTermHighlight(tempDiv, terms, colors, { englishExact });
  }
  // Evidence-chunk highlight for the static snapshot/export — same anchor-based
  // block highlight the live viewer uses (the .evidence-chunk-highlight class is
  // global CSS in index.css, loaded by the snapshot route too).
  if (evidenceChunkText) {
    applyEvidenceChunkHighlight(tempDiv, evidenceChunkText);
  }
  // A static export/snapshot has NO React click handler, so internal section
  // cross-refs (<a href="#" data-section=…>) and footnote refs would be dead
  // links that jump the page to the top. Demote those to non-link styled text
  // (keep the indigo cue). External https links resolve natively in a static
  // file, so leave them clickable.
  tempDiv.querySelectorAll('a[data-section], a[data-footnote], a[data-backref]').forEach((a) => {
    const span = document.createElement('span');
    span.setAttribute('style', 'color:#14746D');
    span.innerHTML = (a as HTMLElement).innerHTML;
    a.replaceWith(span);
  });
  return tempDiv.innerHTML;
}

/**
 * SPL Content Renderer Component
 *
 * Renders SPL XML content as properly formatted HTML, with fallback to plain text.
 */
export const SplContentRenderer: React.FC<SplContentRendererProps> = ({
  contentXml,
  contentText,
  drugName,
  className = '',
  highlightFn,
  highlightTerms = [],
  highlightColors,
  englishExact = false,
  contentHtml,
  evidenceChunkText,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Every shell tab stays mounted: section and footnote ids repeat across
  // tabs, so lookups are scoped to this tab's pane (the document outside one).
  const { rootRef } = useTab();

  const htmlContent = useMemo(() => {
    let result: string | null = null;

    // Priority 1: SPL XML content (FDA labels)
    if (contentXml) {
      const transformedHtml = transformSplXmlToHtml(contentXml);
      result = DOMPurify.sanitize(transformedHtml, {
        ALLOWED_TAGS: [...ALLOWED_TAGS, 'mark'],
        ALLOWED_ATTR,
        ADD_ATTR: ['loading', 'onerror', 'data-section', 'data-footnote', 'data-backref', 'id'],
      });
    }
    // Priority 2: Pre-formatted HTML (DOCX drafts with tables)
    else if (contentHtml) {
      result = DOMPurify.sanitize(contentHtml, {
        ALLOWED_TAGS: [...ALLOWED_TAGS, 'mark'],
        ALLOWED_ATTR: [...ALLOWED_ATTR, 'style'],
        ADD_ATTR: ['style'],
      });
    }

    if (!result) return null;

    // Apply both highlighting passes in a single DOM parse+serialize cycle
    if (evidenceChunkText || highlightTerms.length > 0) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = result;

      // 1. Evidence chunk: add CSS class to matching block elements
      if (evidenceChunkText) {
        applyEvidenceChunkHighlight(tempDiv, evidenceChunkText);
      }

      // 2. Search terms: wrap matching words with <mark> tags
      if (highlightTerms.length > 0) {
        applySearchTermHighlight(tempDiv, highlightTerms, highlightColors, { englishExact });
      }

      result = tempDiv.innerHTML;
    }

    return result;
  }, [contentXml, contentHtml, drugName, highlightTerms, highlightColors, evidenceChunkText, englishExact]);

  // Handle clicks on section links and footnote links
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Handle footnote reference links (clicking [1] in text to go to footnote)
      const footnoteLink = target.closest('a[data-footnote]') as HTMLAnchorElement;
      if (footnoteLink) {
        e.preventDefault();
        const fnId = footnoteLink.dataset['footnote'];
        if (fnId) {
          const footnoteEl = findInTab(rootRef, `fn-${fnId}`);
          if (footnoteEl) {
            footnoteEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight the footnote briefly
            footnoteEl.classList.add('bg-indigo-50', 'transition-colors', 'duration-300');
            setTimeout(() => footnoteEl.classList.remove('bg-indigo-50'), 2000);
          }
        }
        return;
      }

      // Handle back-link from footnote to reference (clicking [1] in footnote to go back)
      const backLink = target.closest('a[data-backref]') as HTMLAnchorElement;
      if (backLink) {
        e.preventDefault();
        const fnId = backLink.dataset['backref'];
        if (fnId) {
          const refEl = findInTab(rootRef, `fnref-${fnId}`);
          if (refEl) {
            refEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight the reference briefly
            refEl.classList.add('bg-indigo-100', 'transition-colors', 'duration-300');
            setTimeout(() => refEl.classList.remove('bg-indigo-100'), 2000);
          }
        }
        return;
      }

      // Handle section links
      const link = target.closest('a[data-section]') as HTMLAnchorElement;
      if (!link) return;

      e.preventDefault();
      const sectionNum = link.dataset['section'];
      if (!sectionNum) return;

      // Find the section element by looking for section headers with matching section number
      // Try multiple selector patterns to find the section
      const selectors = [
        `[id*="section-"][id*="${sectionNum}"]`, // id contains section number
        `section[data-section="${sectionNum}"]`, // data attribute on section
        `[data-section-number="${sectionNum}"]`, // explicit section number attribute
      ];

      const scope: ParentNode = rootRef.current ?? document;
      let targetSection: Element | null = null;
      for (const selector of selectors) {
        targetSection = scope.querySelector(selector);
        if (targetSection) break;
      }

      // Fallback: search for section header containing the section number in title
      if (!targetSection) {
        const allSections = scope.querySelectorAll('section[id^="section-"]');
        for (const section of allSections) {
          const header = section.querySelector('h2, h3, h4');
          if (header?.textContent?.includes(sectionNum)) {
            targetSection = section;
            break;
          }
        }
      }

      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Expand section if collapsed
        const collapseBtn = targetSection.querySelector('button[aria-expanded="false"]');
        if (collapseBtn) {
          (collapseBtn as HTMLButtonElement).click();
        }
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [htmlContent, rootRef]);

  // If we have HTML content, render it
  if (htmlContent) {
    return (
      <div
        ref={containerRef}
        className={`spl-content prose prose-slate prose-sm max-w-none ${className}`}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  }

  // Fallback to plain text with optional highlighting
  if (contentText) {
    if (highlightFn) {
      return (
        <div className={`whitespace-pre-wrap text-slate-700 ${className}`}>
          {highlightFn(contentText)}
        </div>
      );
    }
    if (englishExact && highlightTerms.length) {
      const ranges = findTermRanges(contentText, highlightTerms, true);
      const nodes: React.ReactNode[] = [];
      let pos = 0;
      ranges.forEach((r, i) => {
        nodes.push(contentText.slice(pos, r.start));
        const color = highlightColors?.[r.termIndex];
        nodes.push(<mark key={i} className="search-highlight px-0.5 rounded" style={color ? { backgroundColor: color } : undefined}>{contentText.slice(r.start, r.end)}</mark>);
        pos = r.end;
      });
      nodes.push(contentText.slice(pos));
      return <div className={`whitespace-pre-wrap text-slate-700 ${className}`}>{nodes}</div>;
    }
    return (
      <div className={`whitespace-pre-wrap text-slate-700 ${className}`}>
        {contentText}
      </div>
    );
  }

  // No content
  return null;
};

export default SplContentRenderer;
